// ts-rest contract for the document-draft review surface.
//
// Drafts are created by the MCP document tools (`create_document_draft`) and
// land `pending`; staff review them here. Read-only for now — approve / reject
// follow the Reviewable lifecycle and land with the review actions.
//
// Deliberately NOT under `/documents` — that prefix already serves the retained
// client-upload surface, whose `/documents/:clientId` route would swallow a
// `/documents/drafts` path.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBody } from '../schemas/common';

const c = initContract();

export const documentDraftSchema = z.object({
    id: z.string(),
    templateSlug: z.string(),
    templateVersionTag: z.string(),
    subjectKind: z.enum(['call', 'client']),
    subjectId: z.string(),
    clientId: z.string().nullable(),
    title: z.string(),
    fields: z.record(z.string(), z.unknown()),
    filledVia: z.enum(['mcp', 'agent', 'human']),
    filledByLabel: z.string().nullable(),
    requestedByUserId: z.string(),
    requestedAt: z.string(),
    reviewStatus: z.string().nullable(),
    // The outcome of the review, so the page can show WHY a draft was refused
    // or sent back rather than just that it was.
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    rejectionReason: z.string().nullable(),
    revisionReason: z.string().nullable(),
    revisionCount: z.number().nullable(),
});
export type DocumentDraftDto = z.infer<typeof documentDraftSchema>;

export const documentDraftsContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/document-drafts',
            responses: {
                200: z.object({ drafts: z.array(documentDraftSchema) }),
                403: errorBody,
            },
            summary: 'Document drafts visible to the caller, newest first',
        },
        get: {
            method: 'GET',
            path: '/document-drafts/:id',
            pathParams: z.object({ id: z.string() }),
            responses: { 200: documentDraftSchema, 403: errorBody, 404: errorBody },
            summary: 'One document draft, with its filled fields',
        },
        approve: {
            method: 'POST',
            path: '/document-drafts/:id/approve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ approvalNote: z.string().max(1_000).optional() }),
            responses: {
                200: documentDraftSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Approve a draft (pending → approved)',
        },
        reject: {
            method: 'POST',
            path: '/document-drafts/:id/reject',
            pathParams: z.object({ id: z.string() }),
            // A reason is required: a rejected regulatory document with no
            // recorded reason is a gap in the audit trail.
            body: z.object({ rejectionReason: z.string().min(1).max(1_000) }),
            responses: {
                200: documentDraftSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Reject a draft (pending → rejected)',
        },
        requestRevision: {
            method: 'POST',
            path: '/document-drafts/:id/request-revision',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ revisionReason: z.string().min(1).max(1_000) }),
            responses: {
                200: documentDraftSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Send a draft back for changes (pending → revision_requested)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
